import React from 'react';
import { Box, TextField, MenuItem, Typography, InputAdornment } from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  Place as PlaceIcon,
  Business as BusinessIcon,
  Hotel as BedIcon,
  Map as MapIcon,
  Explore as GpsIcon,
  MarkunreadMailbox as PostIcon,
} from '@mui/icons-material';

import { BRAND, brandFieldSx } from '../../../brand';
import { FACILITY_TYPES, OWNERSHIP_TYPES, REGIONS } from '../options';
import StepHeading from '../StepHeading';
import { FormSection, FormRow, Field } from '../FormLayout';

// ============================================================================
//  Step 1 — Facility Profile
// ============================================================================
//  Who the system belongs to. `name` and `type` in particular are not just
//  labels: the name is stamped on every printed receipt, ID card and claim,
//  and the type drives the NHIS tariff tier the claims module applies.
//
//  Laid out in two sections — Identification, then Location — so the operator
//  works through one idea at a time rather than a wall of eighteen boxes.

function FacilityStep({ data, errors, onChange }) {
  const set = (field) => (e) => onChange(field, e.target.value);

  const adorn = (Icon) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Icon sx={{ fontSize: 19, color: BRAND.navy }} />
      </InputAdornment>
    ),
  });

  return (
    <Box>
      <StepHeading
        icon={HospitalIcon}
        title="Facility Profile"
        subtitle="Register the hospital or clinic this installation will serve. Use the name exactly as it appears on your operating licence — it is printed on receipts, ID cards and NHIS claims."
      />

      {/* ── Identification ──────────────────────────────────────────────── */}
      <FormSection
        first
        title="Identification"
        caption="How the facility is named and classified throughout the system."
      >
        <FormRow>
          <Field span={12}>
            <TextField
              fullWidth required
              label="Facility Name"
              placeholder="e.g. Gigatt Apex Memorial Hospital"
              value={data.name}
              onChange={set('name')}
              error={!!errors.name}
              helperText={errors.name || 'The registered name of the hospital, clinic or health centre'}
              sx={brandFieldSx}
              InputProps={adorn(HospitalIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              select fullWidth required
              label="Facility Type"
              value={data.type}
              onChange={set('type')}
              error={!!errors.type}
              helperText={errors.type || 'Determines the NHIS tariff tier applied to claims'}
              sx={brandFieldSx}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 340 } } } }}
            >
              {FACILITY_TYPES.map((t) => (
                <MenuItem key={t} value={t} sx={{ whiteSpace: 'normal', maxWidth: 460 }}>{t}</MenuItem>
              ))}
            </TextField>
          </Field>

          <Field span={6}>
            <TextField
              select fullWidth
              label="Ownership"
              value={data.ownership}
              onChange={set('ownership')}
              error={!!errors.ownership}
              helperText={errors.ownership || 'Optional — public, private, mission or quasi-government'}
              sx={brandFieldSx}
              InputProps={adorn(BusinessIcon)}
            >
              {OWNERSHIP_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
          </Field>
        </FormRow>
      </FormSection>

      {/* ── Location ────────────────────────────────────────────────────── */}
      <FormSection
        title="Location"
        caption="Where the facility sits. Used on printed documents and in regional reporting."
      >
        <FormRow>
          <Field span={4}>
            <TextField
              select fullWidth required
              label="Region"
              value={data.region}
              onChange={set('region')}
              error={!!errors.region}
              helperText={errors.region || ' '}
              sx={brandFieldSx}
              InputProps={adorn(MapIcon)}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 340 } } } }}
            >
              {REGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
          </Field>

          <Field span={4}>
            <TextField
              fullWidth
              label="District / Municipality"
              placeholder="e.g. Tepa Municipal"
              value={data.district}
              onChange={set('district')}
              error={!!errors.district}
              helperText={errors.district || 'Optional'}
              sx={brandFieldSx}
            />
          </Field>

          <Field span={4}>
            <TextField
              fullWidth required
              label="Town / Location"
              placeholder="e.g. Tepa"
              value={data.location}
              onChange={set('location')}
              error={!!errors.location}
              helperText={errors.location || ' '}
              sx={brandFieldSx}
              InputProps={adorn(PlaceIcon)}
            />
          </Field>

          <Field span={4}>
            <TextField
              fullWidth
              label="Ghana Post GPS"
              placeholder="e.g. AS-1234-5678"
              value={data.digitalAddress}
              onChange={set('digitalAddress')}
              error={!!errors.digitalAddress}
              helperText={errors.digitalAddress || 'Optional — digital address'}
              sx={brandFieldSx}
              InputProps={adorn(GpsIcon)}
            />
          </Field>

          <Field span={4}>
            <TextField
              fullWidth
              label="Postal Address"
              placeholder="e.g. P.O. Box 45, Tepa"
              value={data.poBox}
              onChange={set('poBox')}
              error={!!errors.poBox}
              helperText={errors.poBox || 'Optional'}
              sx={brandFieldSx}
              InputProps={adorn(PostIcon)}
            />
          </Field>

          <Field span={4}>
            <TextField
              fullWidth type="number"
              label="Bed Capacity"
              placeholder="e.g. 60"
              value={data.bedCapacity}
              onChange={set('bedCapacity')}
              error={!!errors.bedCapacity}
              helperText={errors.bedCapacity || 'Optional — used by ward occupancy reporting'}
              sx={brandFieldSx}
              InputProps={adorn(BedIcon)}
            />
          </Field>
        </FormRow>
      </FormSection>

      <Typography sx={{ mt: 4, fontSize: 12.5, color: BRAND.inkFaint, lineHeight: 1.7 }}>
        Fields marked <Box component="span" sx={{ color: BRAND.danger }}>*</Box> are required. Everything
        here can be edited later from <strong>Admin Panel → Facility Settings</strong>.
      </Typography>
    </Box>
  );
}

export default FacilityStep;
